import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiInterceptor';
import './DashboardPage.scss';

interface DashboardStats {
  totalCourses: number;
  completedCourses: number;
  streak: number;
  creditsRemaining: number;
  hoursThisWeek: number;
}

interface RecentCourse {
  id: string;
  title: string;
  progress: number;
  lastAccessed: string;
  currentLesson: string;
  currentLessonNumber: number;
  totalLessons: number;
  difficulty?: string;
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    completedCourses: 0,
    streak: 0,
    creditsRemaining: 0,
    hoursThisWeek: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setCoursesLoading(true);

        // Try to fetch stats, but don't fail if it doesn't work
        try {
          console.log('Fetching dashboard stats...');
          const statsResponse = await api.get('/auth/dashboard-stats');
          console.log('Stats response:', statsResponse);
          
          if (statsResponse?.data?.success && statsResponse.data.data) {
            console.log('Setting stats from wrapped response:', statsResponse.data.data);
            setStats(statsResponse.data.data);
          } else if (statsResponse?.data) {
            console.log('Setting stats from direct response:', statsResponse.data);
            // Handle direct data response without wrapper
            setStats(statsResponse.data);
          }
        } catch (statsError) {
          console.log('Stats not available, using defaults:', statsError);
          // Keep default stats - no error shown to user
        }

        // Try to fetch courses, but don't fail if it doesn't work
        try {
          console.log('Fetching courses...');
          const coursesResponse = await api.get('/courses/my-courses');
          console.log('Courses response:', coursesResponse);
          let courses = [];
          
          if (coursesResponse?.data?.success && coursesResponse.data.data) {
            console.log('Using wrapped courses data:', coursesResponse.data.data);
            courses = coursesResponse.data.data || [];
          } else if (coursesResponse?.data && Array.isArray(coursesResponse.data)) {
            console.log('Using direct array courses data:', coursesResponse.data);
            // Handle direct array response
            courses = coursesResponse.data;
          } else if (coursesResponse?.data) {
            console.log('Unknown courses response format:', coursesResponse.data);
            // Handle other response formats
            courses = [];
          }
          
          // Transform courses data to recent courses format
          const enrolledCourses = courses
            .filter(
              (course: any) =>
                // Include courses that have enrollments OR that have a count indicating enrollment
                (course.enrollments && course.enrollments.length > 0) ||
                (course._count?.enrollments && course._count.enrollments > 0)
            )
            .sort((a: any, b: any) => {
              // Sort by most recent progress activity, fallback to enrollment date
              // Find the most recent progress across all lessons
              const getRecentProgress = (course: any) => {
                if (!course.lessons) return null;

                let mostRecentProgress = null;
                for (const lesson of course.lessons) {
                  if (lesson.progress && lesson.progress.length > 0) {
                    const progress = lesson.progress[0];
                    if (
                      !mostRecentProgress ||
                      new Date(progress.updatedAt) >
                        new Date(mostRecentProgress.updatedAt)
                    ) {
                      mostRecentProgress = progress;
                    }
                  }
                }
                return mostRecentProgress;
              };

              const aProgress = getRecentProgress(a);
              const bProgress = getRecentProgress(b);

              const aDate = aProgress
                ? new Date(aProgress.updatedAt)
                : new Date(a.enrollments?.[0]?.enrolledAt || a.createdAt);
              const bDate = bProgress
                ? new Date(bProgress.updatedAt)
                : new Date(b.enrollments?.[0]?.enrolledAt || b.createdAt);

              return bDate.getTime() - aDate.getTime();
            })
            .slice(0, 3) // Take only first 3
            .map((course: any) => {
              // Handle cases where enrollments array might be empty but count indicates enrollment
              const enrollment = course.enrollments?.[0];
              const totalLessons = course._count?.lessons || 0;
              
              // Default progress if no enrollment data
              const enrollmentProgress = enrollment?.progress || 0;
              const currentLessonNumber = Math.min(
                Math.floor((enrollmentProgress / 100) * totalLessons) + 1,
                totalLessons
              );
              const currentLesson =
                course.lessons && course.lessons[currentLessonNumber - 1]
                  ? course.lessons[currentLessonNumber - 1].title
                  : 'Ready to start';

              // Find the most recent progress across all lessons
              const getRecentProgress = (course: any) => {
                if (!course.lessons) return null;

                let mostRecentProgress = null;
                for (const lesson of course.lessons) {
                  if (lesson.progress && lesson.progress.length > 0) {
                    const progress = lesson.progress[0];
                    if (
                      !mostRecentProgress ||
                      new Date(progress.updatedAt) >
                        new Date(mostRecentProgress.updatedAt)
                    ) {
                      mostRecentProgress = progress;
                    }
                  }
                }
                return mostRecentProgress;
              };

              const recentProgress = getRecentProgress(course);
              const lastAccessedDate = recentProgress
                ? recentProgress.updatedAt
                : (enrollment?.enrolledAt || course.createdAt);

              return {
                id: course.id,
                title: course.title,
                progress: Math.round(enrollmentProgress),
                lastAccessed: formatLastAccessed(lastAccessedDate),
                currentLesson,
                currentLessonNumber,
                totalLessons,
                difficulty: course.difficulty,
              };
            });

          setRecentCourses(enrolledCourses);
        } catch (coursesError) {
          console.log('Courses not available, using empty array:', coursesError);
          // Keep empty courses array - no error shown to user
        }
      } catch (error) {
        console.log('General dashboard error:', error);
        // Don't show error to user, just log it
      } finally {
        setLoading(false);
        setCoursesLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatLastAccessed = (enrolledAt: string): string => {
    const now = new Date();
    const enrolled = new Date(enrolledAt);
    const diffMs = now.getTime() - enrolled.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return enrolled.toLocaleDateString();
  };

  const [recentCourses, setRecentCourses] = useState<RecentCourse[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Page Header */}
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Track your progress and continue learning</p>
        </div>


        {/* Stats Grid */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Day Streak</div>
              <div className="stat-value">{loading ? '...' : stats.streak}</div>
              <div className="stat-subtext">
                {loading
                  ? 'Loading...'
                  : stats.streak > 0
                    ? 'Keep it going!'
                    : 'Start your streak!'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Credits Left</div>
              <div className="stat-value">
                {loading
                  ? '...'
                  : stats.creditsRemaining === 999
                    ? '∞'
                    : stats.creditsRemaining}
              </div>
              <div className="stat-subtext">
                {loading
                  ? 'Loading...'
                  : stats.creditsRemaining > 0
                    ? 'Generate more courses'
                    : 'Upgrade for more'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Completed</div>
              <div className="stat-value">
                {loading
                  ? '...'
                  : `${stats.completedCourses}/${stats.totalCourses}`}
              </div>
              <div className="stat-subtext">
                {loading
                  ? 'Loading...'
                  : stats.totalCourses > 0
                    ? `${Math.round((stats.completedCourses / stats.totalCourses) * 100)}% done`
                    : 'Start learning!'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">This Week</div>
              <div className="stat-value">
                {loading ? '...' : `${stats.hoursThisWeek}h`}
              </div>
              <div className="stat-subtext">
                {loading
                  ? 'Loading...'
                  : stats.hoursThisWeek > 0
                    ? 'Great pace!'
                    : 'Start learning!'}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Courses */}
        <div className="recent-courses-section">
          <div className="section-header">
            <h2>Pick up where you left off</h2>
            <button
              className="view-all-btn"
              onClick={() => navigate('/my-courses')}
            >
              View All Courses
            </button>
          </div>

          <div className="courses-grid">
            {coursesLoading ? (
              // Loading skeleton for courses
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="dashboard-course-card loading">
                  <div className="dashboard-course-header-visual">
                    <div className="dashboard-course-icon">
                      <div className="skeleton-icon"></div>
                    </div>
                  </div>
                  <div className="dashboard-course-content">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-meta"></div>
                  </div>
                </div>
              ))
            ) : recentCourses.length === 0 ? (
              // Empty state
              <div className="empty-courses-state">
                <h3>No courses yet</h3>
                <p>
                  Start learning by enrolling in a course or creating your own!
                </p>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/browse')}
                >
                  Browse Courses
                </button>
              </div>
            ) : (
              // Actual courses
              recentCourses.map((course, index) => (
                <div
                  key={course.id}
                  className="dashboard-course-card"
                  onClick={() => navigate(`/courses/${course.id}`)}
                >
                  <div className="dashboard-course-header-visual">
                    <div className="dashboard-course-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="dashboard-course-content">
                    <div className="dashboard-course-header">
                      <h3 className="dashboard-course-title">{course.title}</h3>
                    </div>

                    <div className="dashboard-course-description">
                      <p>
                        Continue your learning journey with the next lesson in
                        this comprehensive course.
                      </p>
                    </div>

                    <div className="dashboard-current-lesson">
                      <span className="dashboard-lesson-label">
                        Lesson {course.currentLessonNumber}/
                        {course.totalLessons}:
                      </span>
                      <span className="dashboard-lesson-title">
                        {course.currentLesson}
                      </span>
                    </div>

                    <div className="dashboard-course-meta">
                      <span className="dashboard-meta-item">
                        {course.lastAccessed}
                      </span>
                      <span className="dashboard-meta-item">
                        {course.progress}% complete
                      </span>
                      <span className={`dashboard-difficulty-badge ${course.difficulty || 'basic'}`}>
                        {course.difficulty ? course.difficulty.charAt(0).toUpperCase() + course.difficulty.slice(1) : 'Basic'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <div className="actions-grid">
            <div className="action-card">
              <h3>Create New Course</h3>
              <p>Generate a course on any topic that sparks your curiosity.</p>
              <button
                className="btn-primary"
                onClick={() => navigate('/generate')}
              >
                Start Creating
              </button>
            </div>

            <div className="action-card">
              <h3>Browse Curated Courses</h3>
              <p>
                Discover courses from quantum physics to molecular gastronomy.
              </p>
              <button
                className="btn-secondary"
                onClick={() => navigate('/browse')}
              >
                Explore Courses
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
